import React, { FC, useCallback, useEffect, useState } from 'react'

import { observer } from 'mobx-react'
import { FieldErrors, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import cn from 'classnames'
import { useDebouncedCallback } from 'use-debounce'
import * as yup from 'yup'

import { yupResolver } from '@hookform/resolvers/yup'

import { checkBadWords, isAvailableBlockchainProvider } from 'utils'

import { toast } from 'App'
import Button from 'components/Button'
import ButtonIcon from 'components/ButtonIcon'
import ToolTipImg from 'components/ToolTipImg'

import { useStore } from 'store'

import { ReactComponent as CompanyIcon } from 'sources/images/companyIcon.svg'
import warning from 'sources/images/warning.svg'

import styles from './styles.module.scss'

interface IProps {
  setIsOpen: (value: boolean) => void
}

const InitialOfferInfo = {
  AuthencityBalance: 0,
  TotalAuthBacked: 0,
  AverageAuthByUser: 0,
  HighestBidPrice: 0,
}

export const BackTwitterUser: FC<IProps> = observer(({ setIsOpen }) => {
  const {
    mediaQuery,
    walletConnect,
    donate,
    profile,
    authToken,
    loading,
    twitter,
  } = useStore()
  const navigate = useNavigate()
  const [backedBy, setBackedBy] = useState(0)
  const [offerInfo, setOfferInfo] = useState(InitialOfferInfo)
  const [alreadyRegisteredNickname, setAlreadyRegisteredNickname] = useState('')
  // const [timer, setTimer] = useState(0)

  const BackTwitterUserSchema = yup.object().shape({
    twitterhandle: yup
      .string()
      .required('This field is required')
      .min(2, 'Twitterhandle should be longer than 2 letters')
      .test(
        'symbol@',
        'You are not allowed to use symbol "@"',
        (value, context) => {
          const str = value as string
          return !str.match(/@/);
        }
      ),
    amount: yup
      .string()
      .required('This field is required')
      .test('min back', 'Min back 100 AUTH', (value, context) => {
        return Number(value) >= 100
      })
      .test(
        'balance',
        'You do not have this amount of AUTH in your system balance!',
        (value, context) => {
          return offerInfo.AuthencityBalance >= Number(value)
        }
      ),
    // text: yup
    //   .string()
    //   .test('text length', 'Maximum allowed characters 100', (value, context) => {
    //     return value ? value?.length <= 50 : true
    //   })
    //   .test('badWords', 'You are not allowed to use prohibited words', (value, context) => {
    //     return !checkBadWords(value)
    //   })
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(BackTwitterUserSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { twitterhandle: '', amount: '' },
    // defaultValues: { twitterhandle: '', amount: '', text: '' }
  })

  const {
    onChange: onChangeTwitterhandleRegister,
    onBlur: onBlurTwitterhandleRegister,
    name: twitterhandleNameRegister,
    ref: twitterhandleRefRegister,
  } = register('twitterhandle')

  useEffect(() => {
    getInfo()
  }, [])

  // const timeOutCallback = useCallback(() => setTimer(currTimer => currTimer - 1), [])

  // useEffect(() => {
  //   timer > 0 && setTimeout(timeOutCallback, 1000)
  // }, [timer, timeOutCallback])

  const getBackedByUser = async (nickname: string) => {
    try {
      const res = await twitter.getTwitterUserBackingAmount(nickname)
      setBackedBy(res)
    } catch (e) {
      console.log('getBackedByUser', e)
    }
  }

  const getInfo = async () => {
    if (!isAvailableBlockchainProvider()) return

    try {
      const response = await authToken.getBalanceAvailableToClaim()
      setOfferInfo(response)
    } catch (e) {
      toast({
        type: 'warning',
        message: 'Error when retrieving your balance',
      })
    }
  }

  const handlerClickTwitterhandleInput = () => {
    setAlreadyRegisteredNickname('')
    errors.twitterhandle?.message && clearErrors('twitterhandle')
  }

  const handlerClickAmountInput = () => {
    errors.amount?.message && clearErrors('amount')
  }

  // const text = watch('text')
  // const handlerClickTextarea = () => {
  //   errors.text?.message && clearErrors('text')
  // }

  const onSubmit = async (data: Record<string, string>) => {
    if (!profile.selectedUser.Activated) {
      return toast({
        type: 'warning',
        message: 'You need to be Activated to back X (Twitter) users',
      })
    }

    try {
      loading.setLoading(true)
      const response = await donate.createTwitterUserDonate(
        data.twitterhandle,
        Number(data.amount),
        data.text
      )

      if (response.nickname) {
        setAlreadyRegisteredNickname(response.nickname)
      } else {
        // setTimer(30)
        setIsOpen(false)
        toast({
          type: 'success',
          message: 'You have successfully backed profile',
        })
      }
      loading.setLoading(false)
    } catch (e: any) {
      loading.setLoading(false)
      toast({
        type: 'warning',
        message: e.message,
      })
    }
  }

  const onError = (err: FieldErrors) => {
    console.log('err', err)
  }

  const debouncedOnChangeTwitterhandle = useDebouncedCallback(
    (value: string) => {
      getBackedByUser(value)
    },
    1000
  )

  const redirectToProfilePage = () => {
    navigate(`/user/${alreadyRegisteredNickname}`)
    setIsOpen(false)
  }

  return (
    <form
      className={styles.container}
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <div className={styles.header}>
        <div className={styles.title}>Back X (Twitter) user</div>
        <div className={styles.text}>
          To support your favourite X (Twitter) creator, use AUTH to back them
          directly. Enter their Twitter handle, specify the amount, and you're
          set! If they join Authencity, you'll automatically become their
          backer. If not, your AUTH will be refunded after 30 days.
        </div>
      </div>

      <div className={styles.middleBlock}>
        <div
          className={styles.twitterhandleInputBlock}
          onClick={handlerClickTwitterhandleInput}
        >
          {errors.twitterhandle?.message ? (
            <ToolTipImg
              message={String(errors.twitterhandle?.message)}
              src={warning}
            />
          ) : (
            <ButtonIcon
              style={{
                position: 'absolute',
                right: '16px',
                minWidth: mediaQuery.minWidth_2200 ? '20px' : '20px',
                width: mediaQuery.minWidth_2200 ? '20px' : '20px',
                height: mediaQuery.minWidth_2200 ? '20px' : '20px',
              }}
              styleIcon={{
                width: mediaQuery.minWidth_2200 ? '10px' : '10px',
                height: mediaQuery.minWidth_2200 ? '10px' : '10px',
              }}
              icon={'info'}
              tooltip="Please make sure to write correct @twitterhandle of a user you want to back. In case there will be a mistake, your tokens will be returned after 30 days."
              tooltipPosition="top"
            />
          )}
          <span>@</span>
          <input
            placeholder="twitterhandle"
            type="text"
            onChange={event => {
              onChangeTwitterhandleRegister(event)
              debouncedOnChangeTwitterhandle(event.target.value)
            }}
            onBlur={onBlurTwitterhandleRegister}
            name={twitterhandleNameRegister}
            ref={twitterhandleRefRegister}
            className={cn({
              [styles.twitterhandleInput]: true,
              [styles.inputError]: errors.twitterhandle?.message,
            })}
          />
        </div>

        {alreadyRegisteredNickname ? (
          <div className={styles.alreadyRegisteredNicknameBlock}>
            <div className={styles.alreadyRegisteredTitle}>
              This user has already registered on Authencity. Please visit his
              profile:
            </div>
            <div
              className={styles.alreadyRegisteredNicknameLink}
              onClick={redirectToProfilePage}
            >
              {`${process.env.REACT_APP_BASE_URL}user/${alreadyRegisteredNickname}`}
            </div>
          </div>
        ) : null}

        {/*<div onClick={handlerClickTextarea}*/}
        {/*     className={cn({*/}
        {/*       [styles.textAreaBlock]: true,*/}
        {/*       [styles.textAreaBlockError]: errors.text?.message*/}
        {/*     })}>*/}
        {/*  <textarea spellCheck*/}
        {/*            placeholder={'Please input your optional message here, which will be displayed automatically to your backed Twitter creator upon being tagged in an automated backing tweet. Share with them the reasons behind your support.'}*/}
        {/*            className={styles.textarea}*/}
        {/*            {...register('text')}*/}
        {/*            rows={4}/>*/}
        {/*  <div className={cn({*/}
        {/*    [styles.textLength]: true,*/}
        {/*    [styles.textLengthError]: text.length > 50*/}
        {/*  })}>*/}
        {/*    {errors.text?.message*/}
        {/*      ? <ToolTipImg message={String(errors.text?.message)}*/}
        {/*                    src={warning}*/}
        {/*                    style={{ padding: '0 3px 0 0', position: 'unset', display: 'block', height: '16px' }}/>*/}
        {/*      : null}*/}
        {/*    <span>{text.length}/50</span>*/}
        {/*  </div>*/}
        {/*</div>*/}
      </div>

      <div className={styles.bottomBlock}>
        <div className={styles.authSource}>
          <div className={styles.authSourceTitle}>AUTH source:</div>
          <div className={styles.authSourceSwitch}>
            <div className={styles.authSourceSwitchBtn}>System</div>
          </div>
        </div>
        <div className={styles.backBlock}>
          <div className={styles.balanceLabelBlock}>
            <div className={styles.backLabel}>Back this profile by:</div>
            <div className={styles.balance}>
              {`Your system balance is ${offerInfo.AuthencityBalance} AUTH`}
            </div>
          </div>
          <div
            className={styles.amountInputBlock}
            onClick={handlerClickAmountInput}
          >
            <input
              step="0.1"
              type="number"
              {...register('amount')}
              className={cn({
                [styles.amountInput]: true,
                [styles.inputError]: errors.amount?.message,
              })}
            />
            {errors.amount?.message ? (
              <ToolTipImg
                message={String(errors.amount?.message)}
                src={warning}
                style={{ padding: '9px', height: '30px' }}
              />
            ) : (
              <CompanyIcon />
            )}
          </div>
        </div>
        <div className={styles.backingProfile}>
          {backedBy > 0
            ? `You have already backed this profile with: ${backedBy} AUTH`
            : `You have not yet backed this profile`}
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          type={'submit'}
          style={{
            width: '100%',
            height: mediaQuery.minWidth_2200 ? '50px' : '40px',
            borderRadius: 7,
            background:
              'linear-gradient(131.69deg, #1f45c3 6.46%, #0e219b 100%)',
          }}
          styleText={{ color: '#ffffff' }}
          btnStyle="classic"
          text={'Back This Profile'}
        />
      </div>
    </form>
  )
})

BackTwitterUser.displayName = 'BackTwitterUser'
